import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  PaperButtonComponent,
  PaperIconComponent,
  PaperInlineMessageComponent,
} from '@ricoh-paper/angular';
import { MeetingVisitorsService } from '../../../../../core/services/meeting-visitors.service';
import { AnalyticsService } from '../../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../../core/analytics/events';

/**
 * RETIRING PANEL — DO NOT EXTEND. This lives in the Book a Meeting panel,
 * which is being decommissioned; it stays only for the transitional fallback.
 * Visitors on meetings are built on `MeetingVisitorsService` and rendered by
 * `ImMeetingVisitorsComponent` (features/visitors/) in the /schedule event
 * panel and the Calendar (Beta) side panel. Fix bugs here; add nothing.
 */
/**
 * What happened to the visitors on a booking that has just been made.
 *
 * The room is booked either way. Each visitor is its own visit, so a partial
 * failure names exactly who was not invited and why, and Retry sends ONLY
 * those — with the same idempotency keys, so a retry of a visit that did in
 * fact land cannot create a second one.
 */
@Component({
  selector: 'im-bmp-visitors-outcome',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, PaperButtonComponent, PaperIconComponent, PaperInlineMessageComponent],
  host: { class: 'ph-no-capture' },
  template: `
    @if (visitors.outcome(); as outcome) {
      <section class="bvo" data-testid="bmp-visitors-outcome" aria-live="polite">
        @if (outcome.invited.length) {
          <p class="bvo__ok">
            <paper-icon name="check-circle" [size]="14" />
            {{ 'visitors.meeting.invited' | transloco: { count: outcome.invited.length } }}
          </p>
        }
        @if (outcome.failed.length) {
          <paper-inline-message intent="warning">
            {{ 'visitors.meeting.some_failed' | transloco: { count: outcome.failed.length } }}
          </paper-inline-message>
          <ul class="bvo__failed">
            @for (failure of outcome.failed; track failure.draft.key) {
              <li>
                <span class="bvo__name ph-mask">{{ failure.draft.name }}</span>
                <span class="bvo__why">{{ failure.error.message }}</span>
              </li>
            }
          </ul>
          <paper-button
            intent="secondary"
            size="sm"
            icon="refresh"
            [loading]="visitors.inviting()"
            data-testid="bmp-visitors-retry"
            (click)="retry()"
          >
            {{ 'visitors.meeting.retry' | transloco }}
          </paper-button>
        }
        <p class="bvo__hint">{{ 'visitors.done.next_no_email' | transloco }}</p>
      </section>
    }
  `,
  styles: `
    .bvo { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
    .bvo__ok {
      display: inline-flex; align-items: center; gap: 6px;
      margin: 0; font-size: 13px; color: var(--success-text);
    }
    .bvo__failed {
      margin: 0; padding-left: 18px;
      display: flex; flex-direction: column; gap: 4px;
      font-size: 13px; color: var(--text);
    }
    .bvo__name { font-weight: 600; margin-right: 6px; }
    .bvo__why { color: var(--text-secondary); }
    .bvo__hint { margin: 0; font-size: 12px; color: var(--text-tertiary); }
  `,
})
export class BmpVisitorsOutcomeComponent {
  protected readonly visitors = inject(MeetingVisitorsService);
  private readonly analytics = inject(AnalyticsService);

  protected async retry(): Promise<void> {
    const before = this.visitors.outcome()?.failed.length ?? 0;
    const outcome = await this.visitors.retryFailed();
    this.analytics.capture(ANALYTICS_EVENTS.VISITORS_MEETING_RETRIED, {
      booking_id: outcome?.bookingId ?? '',
      retried_count: before,
      still_failed_count: outcome?.failed.length ?? 0,
    });
  }
}
